/**
 * MessageBubble Component
 *
 * Individual message display with chat bubble styling.
 * Supports sent/received alignment, read receipts, reply previews,
 * and message actions (edit, delete, reply, copy).
 */

import React, { useState, useCallback, useMemo } from 'react';
import { formatDistanceToNow, differenceInMinutes } from 'date-fns';
import {
  MoreVertical,
  Check,
  CheckCheck,
  Reply,
  Pencil,
  Trash2,
  Clock,
  AlertCircle,
  Copy,
  RefreshCcw,
  X,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import type { Message, PendingMessage } from '@/types/messaging';

const EDIT_WINDOW_MINUTES = 15;

interface MessageBubbleProps {
  message: Message | PendingMessage;
  isOwn: boolean;
  showAvatar?: boolean;
  showTimestamp?: boolean;
  isGroupMessage?: boolean;
  onReply?: (message: Message) => void;
  onEdit?: (message: Message) => void;
  onDelete?: (messageId: string) => void;
  onRetryPendingMessage?: (tempId: string) => void;
  onRemovePendingMessage?: (tempId: string) => void;
  currentUserId?: string;
}

/**
 * Check if message is a PendingMessage
 */
const isPendingMessage = (message: Message | PendingMessage): message is PendingMessage => {
  return 'tempId' in message && 'status' in message;
};

/**
 * Get user initials
 */
const getInitials = (firstName?: string, lastName?: string, username?: string): string => {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
  if (firstName) {
    return firstName.charAt(0).toUpperCase();
  }
  if (username) {
    return username.charAt(0).toUpperCase();
  }
  return 'U';
};

/**
 * Format timestamp
 */
const formatTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isOwn,
  showAvatar = true,
  showTimestamp = true,
  isGroupMessage = false,
  onReply,
  onEdit,
  onDelete,
  onRetryPendingMessage,
  onRemovePendingMessage,
  currentUserId: _currentUserId,
}) => {
  const [showActions, setShowActions] = useState(false);

  const isPending = isPendingMessage(message);
  const deliveredMessage = isPending ? null : message;
  const isDeleted = !isPending && message.isDeleted;
  const isEdited = !isPending && message.editedAt;
  const isFailed = isPending && message.status === 'failed';

  // Get sender info for received messages
  const sender = !isPending ? message.sender : null;
  const senderName = sender
    ? `${sender.firstName} ${sender.lastName}`.trim() || sender.username
    : '';
  const initials = sender
    ? getInitials(sender.firstName, sender.lastName, sender.username)
    : '';

  // Check if message has been read by recipient
  const isRead = !isPending && message.readBy && message.readBy.length > 0;

  // Reply-to info
  const replyTo = !isPending ? message.replyTo : null;

  // Check if message is within edit window (15 minutes)
  const isWithinEditWindow = useMemo(() => {
    if (isPending || isDeleted) return false;
    const createdAt = new Date(message.createdAt);
    const now = new Date();
    const minutesElapsed = differenceInMinutes(now, createdAt);
    return minutesElapsed < EDIT_WINDOW_MINUTES;
  }, [isPending, isDeleted, message.createdAt]);

  // Copy message to clipboard
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      toast.success('Message copied to clipboard');
    } catch (error) {
      console.error('[MessageBubble] Failed to copy:', error);
      toast.error('Failed to copy message');
    }
  }, [message.content]);

  return (
    <div
      className={`flex gap-2 group ${isOwn ? 'flex-row-reverse' : 'flex-row'}`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {/* Avatar (for received messages) */}
      {!isOwn && showAvatar && sender && (
        <Avatar className="h-8 w-8 shrink-0 mt-1">
          <AvatarImage src={sender.avatar} alt={senderName} />
          <AvatarFallback
            style={{
              backgroundColor: 'hsl(var(--primary) / 0.1)',
              color: 'hsl(var(--primary))',
            }}
            className="text-xs font-medium"
          >
            {initials}
          </AvatarFallback>
        </Avatar>
      )}

      {/* Spacer when no avatar */}
      {!isOwn && !showAvatar && <div className="w-8 shrink-0" />}

      {/* Message content */}
      <div className={`flex flex-col max-w-[70%] ${isOwn ? 'items-end' : 'items-start'}`}>
        {/* Sender name for group messages */}
        {isGroupMessage && !isOwn && sender && (
          <span className="text-xs font-medium text-muted-foreground mb-1 ml-1">
            {senderName}
          </span>
        )}

        {/* Reply preview */}
        {replyTo && (
          <div
            className={`text-xs px-3 py-1.5 mb-1 rounded-lg border-l-2 ${
              isOwn
                ? 'bg-muted/50 border-primary/50 text-muted-foreground'
                : 'bg-muted/30 border-muted-foreground/50 text-muted-foreground'
            }`}
          >
            <span className="font-medium">Replying to:</span>{' '}
            <span className="italic">
              {replyTo.content.substring(0, 50)}
              {replyTo.content.length > 50 ? '...' : ''}
            </span>
          </div>
        )}

        {/* Message bubble */}
        <div
          className={`relative px-4 py-2.5 rounded-2xl ${
            isOwn
              ? 'rounded-br-md'
              : 'rounded-bl-md'
          } ${
            isDeleted
              ? 'bg-muted/50 text-muted-foreground italic'
              : isOwn
              ? 'text-white'
              : 'bg-muted text-foreground'
          } ${isFailed ? 'border border-destructive' : ''}`}
          style={
            !isDeleted && isOwn
              ? { backgroundColor: 'hsl(var(--primary))' }
              : undefined
          }
        >
          {/* Message text */}
          <p className="text-sm whitespace-pre-wrap break-words">
            {message.content}
          </p>

          {/* Timestamp and status */}
          <div
            className={`flex items-center gap-1.5 mt-1 ${
              isOwn ? 'justify-end' : 'justify-start'
            }`}
          >
            {showTimestamp && (
              <span
                className={`text-[10px] ${
                  isOwn && !isDeleted ? 'text-white/70' : 'text-muted-foreground'
                }`}
              >
                {formatTime(message.createdAt)}
              </span>
            )}

            {/* Edited indicator */}
            {isEdited && (
              <span
                className={`text-[10px] ${
                  isOwn && !isDeleted ? 'text-white/70' : 'text-muted-foreground'
                }`}
              >
                (edited)
              </span>
            )}

            {/* Status indicators for own messages */}
            {isOwn && !isDeleted && (
              <>
                {isPending && message.status === 'sending' && (
                  <Clock
                    className={`h-3 w-3 ${
                      isOwn ? 'text-white/70' : 'text-muted-foreground'
                    }`}
                  />
                )}
                {isFailed && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <AlertCircle className="h-3 w-3 text-destructive" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Failed to send. Click to retry.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {!isPending && !isFailed && (
                  <>
                    {isRead ? (
                      <CheckCheck
                        className={`h-3 w-3 ${
                          isOwn ? 'text-white/70' : 'text-muted-foreground'
                        }`}
                        aria-label="Read"
                      />
                    ) : (
                      <Check
                        className={`h-3 w-3 ${
                          isOwn ? 'text-white/70' : 'text-muted-foreground'
                        }`}
                        aria-label="Delivered"
                      />
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* Read receipt tooltip */}
        {isOwn && !isPending && isRead && !isDeleted && (
          <span className="text-[10px] text-muted-foreground mt-0.5">
            Seen {formatDistanceToNow(new Date(message.readBy[0].readAt), { addSuffix: true })}
          </span>
        )}
        {isFailed && (
          <div className="flex items-center gap-1.5 mt-1">
            {onRetryPendingMessage && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => onRetryPendingMessage(message.tempId)}
              >
                <RefreshCcw className="h-3 w-3 mr-1" />
                Retry
              </Button>
            )}
            {onRemovePendingMessage && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-muted-foreground"
                onClick={() => onRemovePendingMessage(message.tempId)}
              >
                <X className="h-3 w-3 mr-1" />
                Remove
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Action menu */}
      {deliveredMessage && !isDeleted && showActions && (
        <div
          className={`self-center opacity-0 group-hover:opacity-100 transition-opacity`}
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
              >
                <MoreVertical className="h-4 w-4" />
                <span className="sr-only">Message actions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={isOwn ? 'end' : 'start'}>
              {onReply && (
                <DropdownMenuItem onClick={() => onReply(deliveredMessage)}>
                  <Reply className="h-4 w-4 mr-2" />
                  Reply
                </DropdownMenuItem>
              )}
              {/* Copy message text */}
              <DropdownMenuItem onClick={handleCopy}>
                <Copy className="h-4 w-4 mr-2" />
                Copy text
              </DropdownMenuItem>
              {/* Edit - only show if within edit window */}
              {isOwn && onEdit && isWithinEditWindow && (
                <DropdownMenuItem onClick={() => onEdit(deliveredMessage)}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
              )}
              {isOwn && onDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onDelete(deliveredMessage.id)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  );
};

export default MessageBubble;
